import { Injectable } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import pdfMake from "pdfmake/build/pdfmake";
import { IDatePlace } from "src/app/models/location.model";
import { MarketModel } from "src/app/models/market.model";
import { OrderModel } from "src/app/models/order.model";
import { ProductModel } from "src/app/models/product.model";
import { ProfileModel } from "src/app/models/profile.model";
import { TourneeModel } from "src/app/models/tournee.model";
import { DateService } from "../date/date.service";
import { ProductService } from "../product/product.service";
import { ShareService } from "../share/share.service";
import { UtilsService } from "../utils/utils.service";

@Injectable({ providedIn: "root" })
export class PdfService {

  styles = {
      title1: {
        fontSize: 40,
        alignment: "center",
        bold: true,
        color: "#ee940d"
      },
      title1sub: {
        fontSize: 14,
        alignment: "center",
        color: "#ee940d"
      },
      title2: {
        fontSize: 20,
        alignment: "left",
        bold: true,
        color: "#003c3a"
      },
      title2sub: {
        fontSize: 18,
        alignment: "justify",
        color: "#003c3a"
      },
      title3: {
        fontSize: 18,
        alignment: "center",
        bold: true,
        color: "#003c3a"
      },
      title4: {
        fontSize: 13,
        bold: true,
        color: "#003c3a"
      },
      link: {
        fontSize: 14,
        decoration: "underline",
        alignment: "center",
        color: "#003c3a"
      },
      title5: {
        fontSize: 9,
        lineHeight: 1,
        bold: true,
        alignment: "left",
        color: "#585757"
      },
      text: {
        fontSize: 9,
        lineHeight: 1,
        alignment: "left",
        color: "#585757"
      },
      textItalic: {
        fontSize: 9,
        lineHeight: 1,
        alignment: "left",
        italics: true,
        color: "#585757"
      },
      textHighlight: {
        fontSize: 10,
        lineHeight: 1.2,
        decoration:"underline",
        bold:true,
        alignment: "left",
        color: "#585757"
      },
      note: {
        fontSize: 10,
        lineHeight: 1.2,
        alignment: "center",
        color: "#585757"
      }
    };
    fonts = {
      FiraSans: {
        normal: "https://firebasestorage.googleapis.com/v0/b/matournee-20bad.appspot.com/o/app%2Ffonts%2FFiraSans-Light.ttf?alt=media&token=8fc9c253-e523-41df-98dc-ad59c22c0a0d",
        italics: "https://firebasestorage.googleapis.com/v0/b/matournee-20bad.appspot.com/o/app%2Ffonts%2FFiraSans-LightItalic.ttf?alt=media&token=76787c84-7039-4fb0-9be1-4cd470743d3b",
        bold:"https://firebasestorage.googleapis.com/v0/b/matournee-20bad.appspot.com/o/app%2Ffonts%2FFiraSans-SemiBold.ttf?alt=media&token=90f90d68-4b76-48c4-b9b1-7cd937f6d24e",
      }
    };


  constructor(public translateService: TranslateService, private dateService:DateService, 
    private productService:ProductService, private utils:UtilsService, private shareService: ShareService) {}

  init() {}

  getBase64(file) {
    return new Promise((resolve, reject) => {
      // const reader = new FileReader();
      // reader.readAsDataURL(file);
      // reader.onload = function () {
      //   resolve(reader.result);
      // };
      // reader.onerror = function (error) {
      //   reject(error);
      // };

      const xhr = new XMLHttpRequest();
      xhr.onload = function () {
        const reader = new FileReader();
        reader.onloadend = function () {
          resolve(reader.result);
        };
        reader.onerror = function (error) {
          reject(error);
        };
        reader.readAsDataURL(xhr.response);
      };
      xhr.open("GET", file);
      xhr.responseType = "blob";
      xhr.send();
    });
  }
  getURLBySize(url: string, size: number): string {
    let extension = '';
    if(url.includes('.png?')){
        extension = '.png';
    }
    else if(url.includes('.jpeg?')){
        extension = '.jpeg';
    }
    else if(url.includes('.jpg?')){
      extension = '.jpg';
    }
    const questionMarkPosition = url.indexOf(extension +'?');
    const endUrl = url.slice(questionMarkPosition);
    let newUrl = url.slice(0,questionMarkPosition);
    newUrl += ('_'+size+'x'+size);
    newUrl += endUrl;
    // console.log('url',url, newUrl, extension, endUrl,questionMarkPosition);
    return newUrl
  }

  async createPdf(
    filename: string,
    name: string,
    subname: string,
    place: string,
    description: string,
    photoURL: string,
    model: string,
    url: string,
    content?: string,
    producer?:string,
    deliveryType?:string,
    producerActivity?:string
  ) {
    if(!photoURL){
      photoURL = '/assets/photos/market.jpg';
    }
    const producerLogoBase64URL = photoURL.slice(0, 7) == "/assets" ? photoURL : this.getURLBySize(photoURL, 300);
    console.log(producerLogoBase64URL);
    // images are loaded and CORS has been updated on Firebase.
    // apparently this could be done directly with URL
    const producerLogoBase64 = await this.getBase64(producerLogoBase64URL);
    //const backgroundBase64 = await this.getBase64("assets/photos/background.jpg");
    const logoBase64 = await this.getBase64("assets/icon/logo.png");
    const line = await this.getBase64("assets/photos/line-thin.png");
    const appStoresBase64 = await this.getBase64("assets/icon/appstores.png");
    const descriptionPieces = this.cutText(description, 450);
    url = url.replace("https://",'');
    url = url.replace("app.",'');
    const urlPieces = url.split('/');
    // console.log(descriptionPieces,urlPieces);
    const styles = {
      title1: {
        fontSize: 40,
        alignment: "center",
        bold: true,
        color: "#ee940d"
      },
      title1sub: {
        fontSize: 14,
        alignment: "center",
        color: "#ee940d"
      },
      title2: {
        fontSize: 26,
        alignment: "center",
        bold: true,
        color: "#003c3a"
      },
      title2sub: {
        fontSize: 18,
        alignment: "justify",
        color: "#003c3a"
      },
      title3: {
        fontSize: 18,
        alignment: "center",
        bold: true,
        color: "#003c3a"
      },
      link: {
        fontSize: 14,
        decoration: "underline",
        alignment: "center",
        color: "#003c3a"
      },
      title4: {
        fontSize: 14,
        lineHeight: 1.2,
        bold: false,
        alignment: "center",
        color: "#585757"
      },
      text: {
        fontSize: 14,
        lineHeight: 1.2,
        alignment: "left",
        color: "#585757"
      },
      note: {
        fontSize: 14,
        lineHeight: 1.2,
        alignment: "center",
        color: "#585757"
      }
    };
    const images = {
      logo: logoBase64,
      photo: producerLogoBase64,
      //background: backgroundBase64,
      appStores: appStoresBase64
    };
    const fonts = {
      OpenSans: {
        normal: "/assets/fonts/FiraSans-Regular.ttf"
      }
    };
    const models = {
      A: [
        { image: logoBase64, width: 150, alignment: "center", margin: [0, 0, 0, 70]  },
        { text: name, style: "title3", margin: [0, 0, 0, 0] },
        { text: this.translateService.instant("poster.BookOnline"), style: "title1", margin: [0, 10, 0, 0] },
        { text: deliveryType == 'tournee'?this.translateService.instant("poster.AndGetDelivered"):this.translateService.instant("poster.AndPickup"), style: "title3", margin: [0, 10, 0, 0] },
        { text: subname, style: "title3", margin: [0, 0, 0, 0] },
        { text: place, style: "title3", margin: [0, 0, 0, 0] },
        { text: this.translateService.instant("poster.FlashOrFollow"), style: "title4", margin: [0, 70, 0, 10] },
        {
          columns: [
            { width:"10%", text:"" },
            { qr: url, foreground: "#003c3a", background: "#ecebe7", fit: 100 },
            {
              stack: [
                { text: 'www.'+urlPieces[0]+'/', style: "link", link: url, margin: [0, 20, 0, 0]  },
                { text: urlPieces[1]+ (urlPieces[2]?'/'+urlPieces[2]:''), style: "link", link: url },
                // { image: appStoresBase64, width: 150, alignment: "center", margin: [0, 10, 0, 20] },
                { text: this.translateService.instant("poster.WithOutIntermediaries"), style: "title1sub", margin: [0, 10, 0, 10] },
              ],
              width: "70%",
              alignment: "center"
            },
            { width:"10%", text:"" }
          ],
          columnGap: 18
        },
        // { text: "  " + this.translateService.instant("poster.WhatIsTourneeDesProducteurs") + "  ", style: "note", margin: [0, 30, 0, 60] },
        { image: line, width:600, alignment: "center", margin: [0, 70, 0, 20]  },
        {
          columns: [
            {stack:[
              { text: producer, style: "title2"},
              { text: producerActivity, style: "title2sub", margin: [0, 0, 0, 10] },
              { text: descriptionPieces[0], style: "text" },              
            ]},
            { image: producerLogoBase64, width: 200, alignment: "center" }
          ],
          columnGap: 18
        },
        { text: descriptionPieces[1], style: "text" }
      ],
      B: [
        // { image: logoBase64, width: 60, alignment: "center" },
        { text: name, style: "title1", margin: [0, 10, 0, 0] },
        { text: subname, style: "title2", margin: [0, 20, 0, 10] },
        {
          columns: [
            { text: descriptionPieces[0], style: "text" },
            { image: producerLogoBase64, width: 200, alignment: "center" }
          ],
          columnGap: 18
        },
        { text: descriptionPieces[1], style: "text" },
        { text: this.translateService.instant("poster.FoundMe"), style: "title2", margin: [0, 10, 0, 10] },
        { text: content, style: "text" },
        {
          stack: [
            { text: this.translateService.instant("poster.BookOnline"), style: "title2", margin: [0, 10, 0, 10] },
            { text: this.translateService.instant("poster.WithOutIntermediaries") + ' ' + this.translateService.instant("common.on"), style: "title2sub", margin: [0, 0, 0, 10] },
            { image: logoBase64, width: 150, alignment: "center" },
            {
              columns: [
                { qr: url, foreground: "#FF9900", background: "#ecebe7", fit: 100 },
                {
                  stack: [
                    { text: 'www.'+urlPieces[0], style: "link", link: url },
                    { text: urlPieces[1], style: "link", link: url },
                    { image: appStoresBase64, width: 150, alignment: "center", margin: [0, 10, 0, 20] }
                  ],
                  width: "75%",
                  alignment: "center",
                  margin: [0, 20, 0, 20]
                }
              ],
              columnGap: 18
            },
            {
              text: "  " + this.translateService.instant("poster.WhatIsTourneeDesProducteurs") + "  ",
              style: "note",
              margin: [0, 10, 0, 0]
            }
          ]
        }
      ],
      C: [
        { image: logoBase64, width: 150, alignment: "center", margin: [0, 0, 0, 70]  },
        { text: name, style: "title3", margin: [0, 0, 0, 0] },
        { text: this.translateService.instant("poster.BookOnline"), style: "title1", margin: [0, 10, 0, 0] },
        { text: this.translateService.instant("poster.ForOurNextSales"), style: "title3", margin: [0, 10, 0, 10] },
        { text: content, style: "text" },
        { text: this.translateService.instant("poster.FlashOrFollow"), style: "title4", margin: [0, 70, 0, 10] },
        {
          columns: [
            { width:"10%", text:"" },
            { qr: url, foreground: "#003c3a", background: "#ecebe7", fit: 100 },
            {
              stack: [
                { text: 'www.'+urlPieces[0]+'/', style: "link", link: url, margin: [0, 20, 0, 0]  },
                { text: urlPieces[1]+ (urlPieces[2]?'/'+urlPieces[2]:''), style: "link", link: url },
                // { image: appStoresBase64, width: 150, alignment: "center", margin: [0, 10, 0, 20] },
                { text: this.translateService.instant("poster.WithOutIntermediaries"), style: "title1sub", margin: [0, 10, 0, 10] },
              ],
              width: "70%",
              alignment: "center"
            },
            { width:"10%", text:"" }
          ],
          columnGap: 18
        },
        // { text: "  " + this.translateService.instant("poster.WhatIsTourneeDesProducteurs") + "  ", style: "note", margin: [0, 30, 0, 60] },
        { image: line, width:600, alignment: "center", margin: [0, 70, 0, 20]  },
        {
          columns: [
            {stack:[
              { text: producer, style: "title2"},
              { text: producerActivity, style: "title2sub", margin: [0, 0, 0, 10] },
              { text: descriptionPieces[0], style: "text" },              
            ]},
            { image: producerLogoBase64, width: 200, alignment: "center" }
          ],
          columnGap: 18
        },
        { text: descriptionPieces[1], style: "text" }
      ],
    };
    const docDefinition = {
      pageSize: "A4",
      pageOrientation: "portrait",
      pageMargins: [40, 40, 40, 30],
      // background: [
      //   {
      //     image: "background",
      //     width: 595.28,
      //     height: 841.89
      //   }
      // ],
      // header: { text: producer, style: "title1", margin: [0, 10, 0, 0] },
      // footer:this.translateService.instant("poster.WhatIsTourneeDesProducteurs"),
      content: models[model],
      defaultStyle:{
        font:'FiraSans'
      },
      images: images,
      styles: styles
    };

    const pdfObject = pdfMake.createPdf(docDefinition,null,this.fonts);
    pdfObject.download(this.translateService.instant("tournee.flyer") + "_" + filename + ".pdf");
  }

  async createProducerPdf(producer: ProfileModel, sales:TourneeModel[]) {
    const filename = producer.details.link;
    const name = producer.details.shortDetails.name;
    let url = this.shareService.getProfileURL(producer);
    const logoBase64 = await this.getBase64("assets/icon/logo.png");
    const appStoresBase64 = await this.getBase64("assets/icon/appstores.png");
    url = url.replace("https://",'');
    url = url.replace("app.",'');
    const urlPieces = url.split('/');
    // console.log(descriptionPieces,urlPieces);
    
    const images = {
      // logo: logoBase64,
      // appStores: appStoresBase64
    };
    const datesPlaces: any[] = [        
      // { text: datesPlacesIntro, style: "title3", margin: [0, 10, 0, 0] },
    ]    
    for(const sale of sales){
      // let datesPlacesIntro = sale.details.deliveryType == 'tournee'
      // ?this.translateService.instant("poster.AndGetDelivered"):this.translateService.instant("poster.AndPickup");
      // datesPlacesIntro += (sale.details.datesPlaces.length > 1)?' :' :'';
      for(const datePlace of sale.details.datesPlaces){
          const place = this.translateService.instant("common.in") + " " + datePlace.geocoding.address.city;
          datesPlaces.push({ text: `- ${place} ${this.dateService.formatFullDate(datePlace, sale.details.recurrence, sale.details.frequency, false)}`, style: "title3", margin: [0, 0, 0, 0] });
      }
    }

    const content =  [
        await this.makeLogo(),
        { text: name, style: "title3", margin: [0, 0, 0, 0] },
        { text: this.translateService.instant("poster.FoundMe"), style: "title1", margin: [0, 10, 0, 10] },
        ...datesPlaces,
        { text: this.translateService.instant("poster.BookOnline"), style: "title1", margin: [0, 20, 0, 0] },
        {
          columns: [
            { width:"10%", text:"" },
            { 
              stack: [
                { qr: url, foreground: "#003c3a", background: "#ecebe7", fit: 100, margin: [0, 0, 0, 0] },
                { text: this.translateService.instant("poster.FlashOrFollow"), style: "title4", margin: [0, 10, 0, 10], fit: 100 },
              ],
              alignment: "center"
            },
            {
              stack: [
                { text: 'www.'+urlPieces[0]+'/', style: "link", link: url, margin: [0, 0, 0, 0]  },
                { text: urlPieces[1]+ (urlPieces[2]?'/'+urlPieces[2]:''), style: "link", link: url },
                // { image: appStoresBase64, width: 150, alignment: "center", margin: [0, 10, 0, 20] },
                { text: this.translateService.instant("poster.WithOutIntermediaries"), style: "title1sub", margin: [0, 10, 0, 10] },
              ],
              width: "70%",
              alignment: "center",
              margin: [0, 20, 0, 0]
            },
            { width:"10%", text:"" }
          ],
          columnGap: 18,
          margin: [0, 10, 0, 10]
        },
        // { text: "  " + this.translateService.instant("poster.WhatIsTourneeDesProducteurs") + "  ", style: "note", margin: [0, 30, 0, 60] },
        await this.makeSeparator(),
        await this.makeProducerCard(producer)
      ];
    this.generateAndDownloadPdf(content, filename, images);
  }

  async createSalePdf(sale: TourneeModel) {
    const filename = sale.details.link;
    const name = sale.details.name;
    let url = this.shareService.getTourneeURL(sale);
    const appStoresBase64 = await this.getBase64("assets/icon/appstores.png");
    url = url.replace("https://",'');
    url = url.replace("app.",'');
    const urlPieces = url.split('/');
    // console.log(descriptionPieces,urlPieces);
    
    const images = {
      // logo: logoBase64,
      // appStores: appStoresBase64
    };
    let datesPlacesIntro = sale.details.deliveryType == 'tournee'
      ?this.translateService.instant("poster.AndGetDelivered"):this.translateService.instant("poster.AndPickup");
    datesPlacesIntro += (sale.details.datesPlaces.length > 1)?' :' :'';
    const datesPlaces: any[] = [        
      { text: datesPlacesIntro, style: "title3", margin: [0, 10, 0, 0] },
    ]
    if(sale.details.datesPlaces.length == 1){
      const datePlace = sale.details.datesPlaces[0];
      const place = this.translateService.instant("common.in") + " " + datePlace.geocoding.address.city;
      datesPlaces.push({ text: this.dateService.formatDatePlace(datePlace,true,true,false), style: "title3", margin: [0, 0, 0, 0] });
      datesPlaces.push({ text: place, style: "title3", margin: [0, 0, 0, 0] });
    }
    else if(sale.details.datesPlaces.length > 1){
      // const ul = [];
      for(const datePlace of sale.details.datesPlaces){
        const place = this.translateService.instant("common.in") + " " + datePlace.geocoding.address.city;
        datesPlaces.push({ text: `- ${place} ${this.dateService.formatFullDate(datePlace, sale.details.recurrence, sale.details.frequency, false)}`, style: "title3", margin: [0, 0, 0, 0] });
      }
      // datesPlaces.push({ul: ul});
    }


    const content =  [
        await this.makeLogo(),
        { text: name, style: "title3", margin: [0, 0, 0, 0] },
        { text: this.translateService.instant("poster.BookOnline"), style: "title1", margin: [0, 10, 0, 0] },
        ...datesPlaces,
        {
          columns: [
            { width:"10%", text:"" },
            { 
              stack: [
                { qr: url, foreground: "#003c3a", background: "#ecebe7", fit: 100, margin: [0, 20, 0, 0] },
                { text: this.translateService.instant("poster.FlashOrFollow"), style: "title4", margin: [0, 10, 0, 10], fit: 100 },
              ],
              alignment: "center"
            },
            {
              stack: [
                { text: 'www.'+urlPieces[0]+'/', style: "link", link: url, margin: [0, 20, 0, 0]  },
                { text: urlPieces[1]+ (urlPieces[2]?'/'+urlPieces[2]:''), style: "link", link: url },
                // { image: appStoresBase64, width: 150, alignment: "center", margin: [0, 10, 0, 20] },
                { text: this.translateService.instant("poster.WithOutIntermediaries"), style: "title1sub", margin: [0, 10, 0, 10] },
              ],
              width: "70%",
              alignment: "center",
              margin: [0, 20, 0, 0]
            },
            { width:"10%", text:"" }
          ],
          columnGap: 18,
          margin: [0, 20, 0, 10]
        },
        // { text: "  " + this.translateService.instant("poster.WhatIsTourneeDesProducteurs") + "  ", style: "note", margin: [0, 30, 0, 60] },
        await this.makeSeparator(),
        sale.details.format == 'solo' ? await this.makeProducerCard(sale.producer) : await this.makeSaleCard(sale)
      ];
    this.generateAndDownloadPdf(content, filename, images);
  }

  async makeSeparator(){
    const line = await this.getBase64("assets/photos/line-thin.png");
    return { image: line, width:600, alignment: "center", margin: [0, 70, 0, 20]  };
  }

  async makeLogo(){
    const logoBase64 = await this.getBase64("assets/icon/logo.png");
    return { image: logoBase64, width: 100, alignment: "center", margin: [0, 0, 0, 70]  }
  }

  async makeProducerCard(producer: ProfileModel){
    const name = producer.details.shortDetails.name;
    const descriptionPieces = this.cutText(producer.details.fullDetails.description, 450);
    const producerActivity = producer.details.shortDetails.job +
      " " +
      this.translateService.instant("common.in") +
      " " +
      producer.details.geocoding.address.city +
      " (" +
      producer.details.geocoding.address.postalCode +
      ")";
    const photoURL =
      producer.details.shortDetails.photoURL || "/assets/photos/" + producer.details.shortDetails.business + ".jpg";
    const producerLogoBase64URL = photoURL.slice(0, 7) == "/assets" ? photoURL : this.getURLBySize(photoURL, 300);
    // images are loaded and CORS has been updated on Firebase.
    // apparently this could be done directly with URL
    const producerLogoBase64 = await this.getBase64(producerLogoBase64URL);

    return {
      stack: [{
        columns: [
          {stack:[
            { text: name, style: "title2"},
            { text: producerActivity, style: "title2sub", margin: [0, 0, 0, 10] },
            { text: descriptionPieces[0], style: "text" },              
          ]},
          { image: producerLogoBase64, width: 200, alignment: "center" }
        ],
        columnGap: 18
      },
      { text: descriptionPieces[1], style: "text" }
    ]}
  }

  async makeSaleCard(sale: TourneeModel | MarketModel){
    const name = sale.details.name;
    const descriptionPieces = this.cutText(sale.details.description, 450);
    const photoURL =
      sale.details.photoURL || "/assets/photos/" + sale.details.type + ".jpg";
    const logoBase64URL = photoURL.slice(0, 7) == "/assets" ? photoURL : this.getURLBySize(photoURL, 300);
    // images are loaded and CORS has been updated on Firebase.
    // apparently this could be done directly with URL
    const logoBase64 = await this.getBase64(logoBase64URL);

    return {
      stack: [{
        columns: [
          {stack:[
            { text: name, style: "title2"},
            { text: descriptionPieces[0], style: "text" },              
          ]},
          { image: logoBase64, width: 200, alignment: "center" }
        ],
        columnGap: 18
      },
      { text: descriptionPieces[1], style: "text" }
    ]}
  }

  async generateAndDownloadPdf(content, filename, images){
    const docDefinition = {
      pageSize: "A4",
      pageOrientation: "portrait",
      pageMargins: [40, 40, 40, 30],
      content: content,
      defaultStyle:{
        font:'FiraSans'
      },
      images: images,
      styles: this.styles
    };

    const pdfObject = pdfMake.createPdf(docDefinition,null,this.fonts);
    pdfObject.download(this.translateService.instant("tournee.flyer") + "_" + filename + ".pdf");
  }

  async exportTourneeOrderToPdf(tournee:TourneeModel, datePlace: IDatePlace, filename) {
    const logoBase64 = await this.getBase64("assets/icon/logo.png") as string;
    const line = await this.getBase64("assets/photos/line-thin.png");
    const images = {
      logo: logoBase64,
      line: line
    };

    const content = [];
    content.push(this.makePageHeader(tournee.details.name,datePlace,logoBase64));
    content.push(this.makeFooterLine(line));

    const ordersArray:any = Object.entries(tournee.orders)
    .filter(order => !['notDelivered','cancelled','rejected'].includes(order[1]['status']))
    .filter(order => (order[1]['details']['datePlace']['date'] == datePlace.date && order[1]['details']['datePlace']['geocoding']['locationID'] == datePlace.geocoding.locationID))
    .sort((a,b)=>{
      if(a[1]['consumer'].key === 'anonymous' || !a[1]['consumer']['details'] || !a[1]['consumer']['details']['fullDetails'] || !a[1]['consumer']['details']['fullDetails']['lastname']){
        return -1
      }
      else if(b[1]['consumer'].key === 'anonymous' || !b[1]['consumer']['details'] || !b[1]['consumer']['details']['fullDetails'] || !b[1]['consumer']['details']['fullDetails']['lastname']){
        return 1
      }
      else{
        return a[1]['consumer']['details']['fullDetails']['lastname'].toLowerCase()<b[1]['consumer']['details']['fullDetails']['lastname'].toLowerCase() ? -1: 1;
      }
    });

    for(const [key,value] of ordersArray){
      const orderStack = {
        stack:[],
        unbreakable:true
      }
      const order = value as OrderModel;
      const consumerName = this.utils.cleanName(order.consumer,true,true) //order.consumer.key == 'anonymous'?(this.translateService.instant('consumer.AnonymousClient')) :order.consumer.details.fullDetails.lastname.toUpperCase() + " " + order.consumer.details.fullDetails.firstname;
      const consumerPhone = order.consumer.details?.fullDetails?.phone || order.consumer.details?.fullDetails?.gsm || '';
      const header = this.makeHeaderLine(consumerName, 
        this.translateService.instant('orderStatus.consumer.'+order.status),
        this.translateService.instant('order.orderDate')+' : ' + this.dateService.formatToShortDateTime(order.details.dateOrdered,true),
        this.translateService.instant('order.PhoneAbs')+' : ' + consumerPhone,
        this.dateService.formatDatePlace(order.details.datePlace, true, false,true)
        )
      orderStack.stack.push(header)

      const productsArray:any = Object.entries(order.products.references);
      for(const [key,value] of productsArray){
        const line = this.makeOrderLine(value,tournee.details.currency);
        orderStack.stack.push(line);
      }
      const total = this.makeTotalLine(this.translateService.instant('tournee.Total'),'',this.productService.convertCurrency(order.finances.totals.sold,tournee.details.currency))
      orderStack.stack.push(total);
      const comment = this.makeCommentLine(order.details.comment);
      orderStack.stack.push(comment);
      orderStack.stack.push(this.makeFooterLine(line));
      content.push(orderStack);
    }
    
    const docDefinition = {
      pageSize: "A4",
      pageOrientation: "portrait",
      pageMargins: [40, 40, 40, 30],
      content: content,
      images: images,
      styles: this.styles,
      defaultStyle:{
        font:'FiraSans'
      }
    };
    // console.log(content);
    const pdfObject = pdfMake.createPdf(docDefinition,null,this.fonts);
    pdfObject.download(filename + ".pdf");
  }

  // async exportMarketOrderToPdf(orders:OrderModel[],market:MarketModel,datePlace: IDatePlace, filename) {
  //   const logoBase64 = await this.getBase64("assets/icon/logo.png") as string;
  //   const line = await this.getBase64("assets/photos/line-thin.png");
  //   const images = {
  //     logo: logoBase64,
  //     line: line
  //   };
  //   const content = <any>[];
  //   content.push(this.makePageHeader(market.details.name,datePlace,logoBase64));
  //   content.push(this.makeFooterLine(line));
  //   console.log(orders);
  //   const ordersSortedByConsumerArray = orders
  //     .filter(order => !['notDelivered','cancelled','rejected'].includes(order['status']))
  //     .sort((a,b)=>{
  //       if(a['consumer'].key === 'anonymous' || !a['consumer'] || !a['consumer']['details'] 
  //           || !a['consumer']['details']['fullDetails'] || !a['consumer']['details']['fullDetails']['lastname']){
  //         return -1
  //       }
  //       else if(b['consumer'].key === 'anonymous' || !b['consumer'] || !b['consumer']['details'] 
  //           || !b['consumer']['details']['fullDetails'] || !b['consumer']['details']['fullDetails']['lastname']){
  //         return 1
  //       }
  //       else{
  //         return a['consumer']['details']['fullDetails']['lastname'].toLowerCase()<b['consumer']['details']['fullDetails']['lastname'].toLowerCase() ? -1: 1;
  //       }
  //     });
  //   for(const order of ordersSortedByConsumerArray){
  //     // console.log(consumer);
  //     const consumerStack = {
  //       stack:[],
  //       unbreakable:true
  //     };
  //     const consumerName = order.consumer.key == 'anonymous'?(this.translateService.instant('consumer.AnonymousClient')) :order.consumer.details.fullDetails.lastname.toUpperCase() + " " + order.consumer.details.fullDetails.firstname;
  //     const consumerPhone = order.consumer.details?.fullDetails?.phone || order.consumer.details?.fullDetails?.gsm || '';
  //     const consumerEmail = order.consumer.details?.fullDetails?.email || '';
  //     const consumerHeader = this.makeHeaderLine(
  //       consumerName, 
  //       '',
  //       this.translateService.instant('order.orderDate')+' : ' + this.dateService.formatToShortDateTime(order.details.dateOrdered,true),
  //       this.translateService.instant('order.PhoneAbs')+' : ' + consumerPhone,
  //       this.dateService.getDatePlaceLabel(order.details.datePlace)
  //     );
  //     consumerStack.stack.push(consumerHeader);

  //     const productsArray:any = Object.entries(order.products.references);
  //     const productsLine = [];
  //     for(const [key,value] of productsArray){
  //       const line = this.makeOrderLine(value,order.details.currency);
  //       productsLine.push(line);
  //     }
  //     consumerStack.stack.push(productsLine);
  //     consumerStack.stack.push(this.makeTotalLine(order.producer.details.shortDetails.name,this.translateService.instant('orderStatus.consumer.'+order.status),this.productService.convertCurrency(order.finances.totals.sold,order.details.currency)));

  //     const footer = this.makeFooterLine(line)
  //     consumerStack.stack.push(footer);
  //     content.push(consumerStack);
  //   }
    
  //   const docDefinition = {
  //     pageSize: "A4",
  //     pageOrientation: "portrait",
  //     pageMargins: [40, 40, 40, 30],
  //     content: content,
  //     defaultStyle:{
  //       font:'FiraSans'
  //     },
  //     images: images,
  //     styles: this.styles
  //   };
  //   // console.log(content);
  //   const pdfObject = pdfMake.createPdf(docDefinition,null,this.fonts);
  //   pdfObject.download(filename + ".pdf");
  // }

  async exportMarketOrderToPdf(ordersByConsumer:any,market:MarketModel,datePlace: IDatePlace, filename) {
    const logoBase64 = await this.getBase64("assets/icon/logo.png") as string;
    const line = await this.getBase64("assets/photos/line-thin.png");
    const images = {
      logo: logoBase64,
      line: line
    };
    const content = <any>[];
    content.push(this.makePageHeader(market.details.name,datePlace,logoBase64));
    content.push(this.makeFooterLine(line));
    console.log(ordersByConsumer);
    const consumersArray:any = Object.entries(ordersByConsumer).sort((a,b)=>{
      if(a[1]['orders'][0]['consumer'].key === 'anonymous' || !a[1]['orders'][0]['consumer'] || !a[1]['orders'][0]['consumer']['details'] || !a[1]['orders'][0]['consumer']['details']['fullDetails'] || !a[1]['orders'][0]['consumer']['details']['fullDetails']['lastname']){
        return -1
      }
      else if(b[1]['orders'][0]['consumer'].key === 'anonymous' || !b[1]['orders'][0]['consumer'] || !b[1]['orders'][0]['consumer']['details'] || !b[1]['orders'][0]['consumer']['details']['fullDetails'] || !b[1]['orders'][0]['consumer']['details']['fullDetails']['lastname']){
        return 1
      }
      else{
        return a[1]['orders'][0]['consumer']['details']['fullDetails']['lastname'].toLowerCase()<b[1]['orders'][0]['consumer']['details']['fullDetails']['lastname'].toLowerCase() ? -1: 1;
      }
    });;
    for(const [key,consumer] of consumersArray){
      // console.log(consumer);
      const consumerStack = {
        stack:[],
        unbreakable:true
      };
      const consumerName = consumer.orders[0].consumer.key == 'anonymous'?(this.translateService.instant('consumer.AnonymousClient')) :consumer.orders[0].consumer.details.fullDetails.lastname.toUpperCase() + " " + consumer.orders[0].consumer.details.fullDetails.firstname;
      const consumerPhone = consumer.orders[0].consumer.details?.fullDetails?.phone || consumer.orders[0].consumer.details?.fullDetails?.gsm || '';
      const consumerEmail = consumer.orders[0].consumer.details?.fullDetails?.email || '';
      const consumerHeader = this.makeHeaderLine(
        consumerName, 
        '',
        this.translateService.instant('order.orderDate')+' : ' + this.dateService.formatToShortDateTime(consumer.orders[0].details.dateOrdered,true),
        this.translateService.instant('order.PhoneAbs')+' : ' + consumerPhone,
        this.dateService.getDatePlaceLabel(consumer.orders[0].details.datePlace)
      );
      consumerStack.stack.push(consumerHeader);
      const orders = consumer.orders.filter(order => !['notDelivered','cancelled','rejected'].includes(order['status']))
      for(const order of orders){
        consumerStack.stack.push(this.makeTotalLine(order.producer.details.shortDetails.name,this.translateService.instant('orderStatus.consumer.'+order.status),this.productService.convertCurrency(order.finances.totals.sold,consumer.orders[0].details.currency)));

        const productsArray:any = Object.entries(order.products.references);
        const productsLine = [];
        for(const [key,value] of productsArray){
          const line = this.makeOrderLine(value,order.details.currency);
          productsLine.push(line);
        }
        consumerStack.stack.push(productsLine);
        // const footer = this.makeTotalLine(this.translateService.instant('tournee.Total'),this.productService.convertCurrency(order.finances.totals.sold,order.details.currency))
        // consumerStack.stack.push(footer);
      }
      const total = this.makeTotalLine(this.translateService.instant('tournee.Total'),'',this.productService.convertCurrency(consumer.finances.totals.sold,consumer.orders[0].details.currency), "title4")
      consumerStack.stack.push(total);
      // const comment = this.makeCommentLine("");
      // consumerStack.stack.push(comment);
      const footer = this.makeFooterLine(line)
      consumerStack.stack.push(footer);
      content.push(consumerStack);
    }
    
    const docDefinition = {
      pageSize: "A4",
      pageOrientation: "portrait",
      pageMargins: [40, 40, 40, 30],
      content: content,
      defaultStyle:{
        font:'FiraSans'
      },
      images: images,
      styles: this.styles
    };
    // console.log(content);
    const pdfObject = pdfMake.createPdf(docDefinition,null,this.fonts);
    pdfObject.download(filename + ".pdf");
  }

  makePageHeader(name:string,date:IDatePlace,logo: string,){
    const header = {
      columns:[
        { stack:[
            { text: date.geocoding.address.city, style:"title5"},
            { text: name, style: "title2"},
            // { text: this.dateService.formatDatePlace(date,true), style: "title4"},
          ]
        },
        { image: logo, width:120}
      ],
      columnGap:18,
      // margin: [0, 0, 0, 20] 
    }  
    return header;
  }

  makeOrderLine(product:ProductModel, currency:string){
    let quantity = '';
    if(product.basket.volumeDelivered){
      quantity = this.productService.convertQuantity(product.basket.volumeDelivered, product.basket.volumeDeliveredMetric);
    }
    else{
      quantity = this.productService.convertQuantity(product.basket.quantityDelivered||product.basket.quantity, product.details.quantityMetric);
    }
    const pricePerUnit = this.productService.convertCurrencyQuantity(product.details.unitPriceVATIncluded, currency,product.details.unitPriceQuantityMetric);
    const totalPrice = this.productService.convertCurrency(product.basket.totalPriceVATIncluded, currency);
    console.log(product, quantity,pricePerUnit,totalPrice);

    const line =  
      {
        columns: [
          { width:"50%", text:product.details.name, alignement:"left", style:"text"},
          { width:"10%", text:quantity, alignement:"right", style:"text"},
          { width:"5%", text:"@", alignment:"right", style:"text"},
          { width:"15%", text:pricePerUnit, alignment:"right", style:"text"},
          { width:"5%", text: "=", alignment: "right", style:"text"},
          { width:"15%", text: totalPrice, alignment: "right", style:"text"},
        ],
        columnGap: 18
      };
    return line;
  }

  makeHeaderLine(left:string, status:string,subtitleLeft:string, subtitleMiddle:string, subtitleRight: string){ 
    const header = [
      { columns: [
          { width:"70%", text:left, alignement:"left", style:"title4" },
          { width:"30%", text:status, alignment: "right", style:"title5"},
        ],
        columnGap: 18,
        margin: [0, 0, 0, 0]
      },
      {
        columns: [
          { width:"40%", text:subtitleLeft, alignment:"left", style:"title5"},
          { width:"30%", text:subtitleMiddle, alignment: "center", style:"title5"},
          { width:"30%", text:subtitleRight, alignment: "right", style:"title5"},
        ],
        columnGap: 18,
        margin: [0, 0, 0, 0]
      }];
    return header;
  }
  makeTotalLine(left:string, middle:string, right:string, style:string = "textHighlight"){ 
    const footer = 
      {
        columns: [
          { width:"40%", text:left, alignment:"left", style:style},
          { width:"40%", text:middle, alignment:"left", style:style},
          { width:"20%", text:right, alignment: "right", style:style},
        ],
        columnGap: 18,
        margin: [0, 3, 0, 0]
      };
    return footer;
  }
  makeCommentLine(comment:string){ 
    return { text:comment, alignement:"left", style:"textItalic" ,margin: [0, 0, 0, 0]}
  }

  makeFooterLine(separator:any){ 
    const footer = { image: separator, width:600, alignment: "center", margin: [0, 5, 0, 5]  };
    return footer;
  }

  cutText(text, length) {
    const pieces = [];
    let trimmedString = text.slice(0, length);
    let secondPart = "";
    if (length < text.length) {
      const position = trimmedString.lastIndexOf(" ");
      trimmedString = trimmedString.slice(0, position);
      secondPart = text.slice(position + 1);
    }
    pieces.push(trimmedString);
    pieces.push(secondPart);
    return pieces;
  }
}
